/**
 * WhatQuiz 커뮤니티 API 서비스
 * 교사 익명 커뮤니티 시스템과 통신
 */

import { auth } from '../firebase/config';

// API 기본 URL 설정
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const COMMUNITY_API_URL = `${API_BASE_URL}/api/community`;

/**
 * 인증 토큰을 포함한 헤더 생성
 */
const getAuthHeaders = async () => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('로그인이 필요합니다.');
  }

  // 🔧 토큰 강제 갱신 - 로그아웃/로그인 후 옛날 토큰 사용 방지
  const token = await currentUser.getIdToken(true);  // force refresh!
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

/**
 * 기본 헤더 (인증 불필요)
 */
const getBasicHeaders = () => ({
  'Content-Type': 'application/json'
});

/**
 * API 응답 처리 헬퍼
 */
const handleApiResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
  }
  return response.json();
};

/**
 * 커뮤니티 API 서비스 객체
 */
export const communityApi = {
  /**
   * 글 목록 조회
   * @param {Object} params - 쿼리 파라미터
   * @param {number} params.page - 페이지 번호 (기본: 1)
   * @param {number} params.limit - 페이지당 글 수 (기본: 20)
   * @param {string} params.sort - 정렬 방식 ('latest' | 'popular')
   * @returns {Promise<Object>} 글 목록 응답
   */
  getPosts: async ({ page = 1, limit = 20, sort = 'latest' } = {}) => {
    try {
      const url = new URL(`${COMMUNITY_API_URL}/posts`);
      url.searchParams.append('page', page.toString());
      url.searchParams.append('limit', limit.toString());
      url.searchParams.append('sort', sort);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: getBasicHeaders(), // 로그인 불필요
      });

      return await handleApiResponse(response);
    } catch (error) {
      console.error('글 목록 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 개별 글 상세 조회
   * @param {string} postId - 글 ID
   * @returns {Promise<Object>} 글 상세 정보 (댓글 포함)
   */
  getPost: async (postId) => {
    try {
      const response = await fetch(`${COMMUNITY_API_URL}/posts/${postId}`, {
        method: 'GET',
        headers: getBasicHeaders(), // 로그인 불필요
      });

      return await handleApiResponse(response);
    } catch (error) {
      console.error('글 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 새 글 작성
   * @param {Object} postData - 글 데이터
   * @param {string} postData.title - 글 제목
   * @param {string} postData.content - 글 내용
   * @param {string} postData.password - 수정/삭제용 비밀번호
   * @returns {Promise<Object>} 작성된 글 정보
   */
  createPost: async ({ title, content, password }) => {
    try {
      const headers = await getAuthHeaders(); // 로그인 필수

      const response = await fetch(`${COMMUNITY_API_URL}/posts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          title: title.trim(),
          content: content.trim(),
          password: password.trim()
        })
      });

      return await handleApiResponse(response);
    } catch (error) {
      console.error('글 작성 실패:', error);
      throw error;
    }
  },

  /**
   * 댓글 작성
   * @param {string} postId - 글 ID
   * @param {string} content - 댓글 내용
   * @param {string} password - 수정/삭제용 비밀번호
   * @returns {Promise<Object>} 작성된 댓글 정보
   */
  createComment: async (postId, content, password) => {
    try {
      const headers = await getAuthHeaders(); // 로그인 필수

      const response = await fetch(`${COMMUNITY_API_URL}/posts/${postId}/comments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          content: content.trim(),
          password: password.trim()
        })
      });

      return await handleApiResponse(response);
    } catch (error) {
      console.error('댓글 작성 실패:', error);
      throw error;
    }
  },

  /**
   * 글 수정
   * @param {string} postId - 글 ID
   * @param {Object} postData - 수정할 글 데이터
   * @param {string} postData.title - 글 제목
   * @param {string} postData.content - 글 내용
   * @param {string} postData.password - 기존 비밀번호
   * @returns {Promise<Object>} 수정된 글 정보
   */
  updatePost: async (postId, { title, content, password }) => {
    try {
      const headers = await getAuthHeaders(); // 로그인 필수

      const response = await fetch(`${COMMUNITY_API_URL}/posts/${postId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          title: title.trim(),
          content: content.trim(),
          password: password.trim()
        })
      });

      return await handleApiResponse(response);
    } catch (error) {
      console.error('글 수정 실패:', error);
      throw error;
    }
  },

  /**
   * 글 삭제
   * @param {string} postId - 글 ID
   * @param {string} password - 비밀번호
   * @returns {Promise<Object>} 삭제 결과
   */
  deletePost: async (postId, password) => {
    try {
      const headers = await getAuthHeaders(); // 로그인 필수

      const response = await fetch(`${COMMUNITY_API_URL}/posts/${postId}`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({
          password: password.trim()
        })
      });

      return await handleApiResponse(response);
    } catch (error) {
      console.error('글 삭제 실패:', error);
      throw error;
    }
  },

  /**
   * 댓글 수정
   * @param {string} postId - 글 ID
   * @param {string} commentId - 댓글 ID
   * @param {Object} commentData - 수정할 댓글 데이터
   * @param {string} commentData.content - 댓글 내용
   * @param {string} commentData.password - 기존 비밀번호
   * @returns {Promise<Object>} 수정된 댓글 정보
   */
  updateComment: async (postId, commentId, { content, password }) => {
    try {
      const headers = await getAuthHeaders(); // 로그인 필수

      const response = await fetch(`${COMMUNITY_API_URL}/posts/${postId}/comments/${commentId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          content: content.trim(),
          password: password.trim()
        })
      });

      return await handleApiResponse(response);
    } catch (error) {
      console.error('댓글 수정 실패:', error);
      throw error;
    }
  },

  /**
   * 댓글 삭제
   * @param {string} postId - 글 ID
   * @param {string} commentId - 댓글 ID
   * @param {string} password - 비밀번호
   * @returns {Promise<Object>} 삭제 결과
   */
  deleteComment: async (postId, commentId, password) => {
    try {
      const headers = await getAuthHeaders(); // 로그인 필수

      const response = await fetch(`${COMMUNITY_API_URL}/posts/${postId}/comments/${commentId}`, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({
          password: password.trim()
        })
      });

      return await handleApiResponse(response);
    } catch (error) {
      console.error('댓글 삭제 실패:', error);
      throw error;
    }
  },

  /**
   * 커뮤니티 서비스 상태 확인
   * @returns {Promise<Object>} 서비스 상태
   */
  checkHealth: async () => {
    try {
      const response = await fetch(`${COMMUNITY_API_URL}/health`, {
        method: 'GET',
        headers: getBasicHeaders(),
      });

      return await handleApiResponse(response);
    } catch (error) {
      console.error('커뮤니티 서비스 상태 확인 실패:', error);
      throw error;
    }
  }
};

/**
 * 글 작성 폼 유효성 검사
 * @param {string} title - 글 제목
 * @param {string} content - 글 내용
 * @param {string} password - 비밀번호
 * @returns {Object} 검사 결과 { isValid, errors }
 */
export const validatePostForm = (title, content, password) => {
  const errors = {};
  
  // 제목 검사
  if (!title || !title.trim()) {
    errors.title = '제목을 입력해주세요.';
  } else if (title.trim().length > 100) {
    errors.title = '제목은 100자 이하로 작성해주세요.';
  }

  // 내용 검사  
  if (!content || !content.trim()) {
    errors.content = '내용을 입력해주세요.';
  } else if (content.trim().length > 2000) {
    errors.content = '내용은 2000자 이하로 작성해주세요.';
  }

  // 비밀번호 검사
  const passwordValidation = validatePassword(password);
  if (!passwordValidation.isValid) {
    errors.password = passwordValidation.error;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * 댓글 폼 유효성 검사
 * @param {string} content - 댓글 내용
 * @param {string} password - 비밀번호
 * @returns {Object} 검사 결과 { isValid, errors }
 */
export const validateCommentForm = (content, password) => {
  const errors = {};

  // 내용 검사
  if (!content || !content.trim()) {
    errors.content = '댓글 내용을 입력해주세요.';
  } else if (content.trim().length > 500) {
    errors.content = '댓글은 500자 이하로 작성해주세요.';
  }

  // 비밀번호 검사
  const passwordValidation = validatePassword(password);
  if (!passwordValidation.isValid) {
    errors.password = passwordValidation.error;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * 비밀번호 유효성 검사
 * @param {string} password - 비밀번호
 * @returns {Object} 검사 결과 { isValid, error }
 */
export const validatePassword = (password) => {
  if (!password || !password.trim()) {
    return {
      isValid: false,
      error: '비밀번호를 입력해주세요.'
    };
  }

  const trimmedPassword = password.trim();

  if (trimmedPassword.length < 4) {
    return {
      isValid: false,
      error: '비밀번호는 4자 이상 입력해주세요.'
    };
  }

  if (trimmedPassword.length > 12) {
    return {
      isValid: false,
      error: '비밀번호는 12자 이하로 입력해주세요.'
    };
  }

  // 허용된 문자 체크 (영문, 숫자, 특수문자)
  const allowedChars = /^[a-zA-Z0-9!@#$%]+$/;
  if (!allowedChars.test(trimmedPassword)) {
    return {
      isValid: false,
      error: '비밀번호는 영문, 숫자, 특수문자(!@#$%)만 쓸 수 있어요.'
    };
  }

  return {
    isValid: true,
    error: null
  };
};

/**
 * 상대 시간 계산 (예: 5분 전, 2시간 전)
 * @param {string} dateString - ISO 날짜 문자열
 * @returns {string} 상대 시간 문자열
 */
export const getRelativeTime = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return '방금 전';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}분 전`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}시간 전`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}일 전`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks}주 전`;
  }

  // 한 달 이상은 날짜 표시
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * 에러 메시지 한국어 변환
 * @param {Error} error - 에러 객체
 * @returns {string} 사용자 친화적 에러 메시지
 */
export const getErrorMessage = (error) => {
  const fallback = '문제가 생겼어요. 잠시 후 다시 시도해주세요';
  const message = error?.message || fallback;
  
  // 백엔드 에러 메시지 매핑 (해요체 + 다음 행동 안내)
  const errorMap = {
    '로그인이 필요합니다.': '로그인하면 글과 댓글을 쓸 수 있어요',
    '글을 찾을 수 없습니다.': '글을 찾지 못했어요. 목록에서 다시 골라주세요',
    '댓글을 찾을 수 없습니다.': '댓글을 찾지 못했어요. 새로고침 후 다시 확인해주세요',
    '부적절한 내용이 포함되어 있습니다. 수정 후 다시 시도해주세요.': '부적절한 표현이 있어요. 내용을 고친 뒤 다시 올려주세요',
    '글 목록을 조회할 수 없습니다.': '글 목록을 불러오지 못했어요. 잠시 후 다시 시도해주세요',
    '비밀번호를 입력해주세요.': '비밀번호를 입력해주세요',
    '비밀번호는 최소 4자 이상이어야 합니다.': '비밀번호는 4자 이상 입력해주세요',
    '비밀번호는 최대 12자 이하여야 합니다.': '비밀번호는 12자 이하로 입력해주세요',
    '비밀번호에 허용되지 않는 문자가 포함되어 있습니다.': '비밀번호는 영문, 숫자, 특수문자(!@#$%)만 쓸 수 있어요',
    '비밀번호가 일치하지 않습니다.': '비밀번호가 맞지 않아요. 다시 확인해주세요',
    '본인이 작성한 글/댓글만 수정/삭제할 수 있습니다.': '내가 쓴 글과 댓글만 수정하거나 삭제할 수 있어요',
    'Network Error': '네트워크 연결을 확인해주세요',
    'Failed to fetch': '서버에 연결하지 못했어요. 잠시 후 다시 시도해주세요'
  };

  if (errorMap[message]) return errorMap[message];
  // 서버가 내려준 문장이 아니면(HTTP 상태 코드 등 기술적 메시지) 고정 문구로 대체
  if (/^HTTP \d{3}/.test(message)) {
    return /^HTTP 429/.test(message)
      ? '요청이 너무 많아요. 잠시 후 다시 시도해주세요'
      : fallback;
  }
  return message;
};

// 기본 내보내기
export default communityApi; 